import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useEmancipateChildPromo } from '~/hooks/apis/promo/useEmancipateChildPromo';

import { InternalPromoWithDiscounts } from '~/services/PromoService';

export interface EmancipatePromoModalProps {
  open: boolean;
  onClose: () => void;
  childPromo: InternalPromoWithDiscounts;
  parentPromo: InternalPromoWithDiscounts;
  onSuccess: () => void | Promise<void>;
}

export default function EmancipatePromoModal({
  open,
  onClose,
  childPromo,
  parentPromo,
  onSuccess,
}: EmancipatePromoModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const emancipateMutation = useEmancipateChildPromo();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (emancipateMutation.isPending) {
      return;
    }
    setSubmitError(null);
    onClose();
  }, [emancipateMutation.isPending, onClose]);

  const handleConfirm = useCallback(async () => {
    setSubmitError(null);
    try {
      await emancipateMutation.mutateAsync(childPromo.id_promo_code);
      enqueueSnackbar('Promo emancipated successfully.', { variant: 'success' });
      await onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Emancipation failed.';
      setSubmitError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [childPromo.id_promo_code, emancipateMutation, enqueueSnackbar, onClose, onSuccess]);

  const parentId = parentPromo?.id_promo_code;
  const parentHref = parentId ? `/promos/code/${encodeURIComponent(parentId)}` : undefined;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Emancipate this promo?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          Emancipation clears the clone link on this promo code (<strong>cloned_from_promo_id</strong>). After that,
          promo log checks and usage limits are evaluated for <strong>this code only</strong>, not the parent or sibling
          clones.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This does not change discounts or conditions by itself; it only changes how the system ties usage to this code
          versus its clone family.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Current promo: {childPromo.code_name ?? childPromo.id_promo_code}
        </Typography>
        {parentHref ? (
          <Typography variant="body2" color="text.secondary">
            Current parent:{' '}
            <Link to={parentHref} target="_blank" rel="noreferrer">
              {parentPromo.code_name ?? parentId}
            </Link>
          </Typography>
        ) : null}
        {submitError ? (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {submitError}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={emancipateMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleConfirm} disabled={emancipateMutation.isPending}>
          {emancipateMutation.isPending ? 'Working…' : 'Emancipate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
